import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { generateJobUrl, Locale, toRelativePath } from '../../../utils/routing/generate-url';
import { BASE_URL } from '../../../utils/constants/url-constants';
import { getDevSiteOrigin } from '../../../utils/constants/dev-site-origin';
import { usePushIndexUiState } from '../../../hooks/usePushIndexUiState';
import { useSearchFilters, useSearchQuery, useSearchPage } from '../../../contexts/SearchContext';
import { useToast, type ToastAction } from '../../../contexts/ToastContext';
import { useAuth } from '../../../hooks/useAuth';
import { useBrowsedJobTracking } from '../../../hooks/useBrowsedJobTracking';
import { useAppSelector } from '../../../redux/hooks';
import { useUserState } from '../../../query/UserProvider';
import { resolveJobMatchScore } from '../../../utils/job-match/resolve-job-match-score';
import type { SearchInferenceBullet } from '../../../types/job-match';
import { trackEvent, trackJobClick, trackJobShare } from '../../../utils/analytics/analytics';
import { sharePage as sharePageUtil } from '../../../pages/job/@jobId/@seoSlug/utils/shareUtils';
import { useHitState } from './useHitState';
import { useHitActions } from './useHitActions';
import { getBrandColorStyles } from '../utils/brandColorUtils';
import { resolveEffectiveBrandColour } from '../../../utils/company/resolve-effective-brand-colour';
import { getLocationDisplayWithCount } from '../../location/utils/locationUtils';
import { useNewLocationSystemEnabled } from '../../../utils/facets/new-location-system-dev-flag';
import {
  formatRemotenessWithScopes,
  resolveStructuredJobLocations,
  resolveStructuredRemoteScopes,
  type StructuredJobLocationDisplay,
  type StructuredLocationLocale,
  type StructuredRemoteScope,
} from '../../../utils/location/structured-job-location-display';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from '../../../utils/routing/job-url-input';
import { useApplicationStateButtonState, APPLICATION_STATES } from '../../application-state';
import type { DisplayJob } from '../../../types';
import { resolveSearchHitIsTyomarkkinatori } from '../../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import type { ApplicationState } from '../../../types/tracked-job';
import { useFormatMessage } from '../../../i18n-review/client/Msg';

export interface UseHitSharedProps {
  hit: DisplayJob;
  sendEvent?: (eventType: string, hit: DisplayJob, eventName?: string, payload?: object) => void;
}

export interface HitSharedData {
  jobId: string;
  jobUrl: string;
  articleId: string;
  titleId: string;
  jobTitle: string;
  isSaved: boolean;
  isViewed: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  isToolsPage: boolean;
  savedJobPayload: DisplayJob & { id: string };
  appliedAt?: string;
  brandColour?: string;
  isTyomarkkinatori: boolean;
  useBrandColour: boolean;
  leftBorderStyle: React.CSSProperties;
  matchScore: number | null;
  matchScoreData: any;
  /** Present when the donut score comes from recent searches (not profile). */
  matchScoreMeta: { source: 'recent-searches'; bullets: SearchInferenceBullet[] } | null;
  /** True when the user has an auth session (used for match donut CTA: sign-in vs profile). */
  isAuthenticated: boolean;
  onLoginClick: () => void;
  actions: ReturnType<typeof useHitActions> & { handleShare: () => Promise<void> };
  getUrlForIndexUiState: (state: { query: string; refinementList: Record<string, string[]>; page: number }) => string;
  pushIndexUiState: (state: { query: string; refinementList: Record<string, string[]>; page: number }) => void;
  makeRefinementState: (facet: string, value: string | string[]) => { query: string; refinementList: Record<string, string[]>; page: number };
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number, action?: ToastAction) => void;
  browsedJobRef: React.RefObject<HTMLElement>;
  menuRefDesktop: React.RefObject<HTMLDivElement>;
  menuRefMobile: React.RefObject<HTMLDivElement>;
  /** Which ellipses button opened the menu; null when closed. Used so only one dropdown is rendered (desktop vs mobile). */
  menuOpenSource: 'desktop' | 'mobile' | null;
  setMenuOpenSource: (source: 'desktop' | 'mobile' | null) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  buttonState: { label: string; icon: React.ComponentType<{ className?: string }>; color: string } | null;
  locationData: ReturnType<typeof getLocationDisplayWithCount>;
  locationStr: string;
  /**
   * TEC-482: the job's gazetteer-resolved locations, only when the viewer has the
   * `dev_enable_new_location_system` cookie on AND the job carries usable `locationIds`. Null
   * otherwise (flag off, or a pre-TEC-482 job not yet backfilled), in which case cards render the
   * legacy `location` strings via locationData/locationStr exactly as before.
   */
  structuredLocation: StructuredJobLocationDisplay | null;
  /** TEC-482: structured remote scope ("EMEA", "Globaali"), same gating as structuredLocation. */
  remoteScope: StructuredRemoteScope | null;
  /** TEC-482 follow-up: every structured remote scope, not just the first; same gating as structuredLocation. */
  remoteScopes: StructuredRemoteScope[];
  /** hit.remoteness with the structured scope(s) appended when there are any: "Etätyö (EMEA)", or
   * "Remote (Germany, Ireland, Spain, United Kingdom)" for a job with several. */
  remotenessLabel: string;
  filters: any;
  hit: DisplayJob;
  sendEvent?: (eventType: string, hit: DisplayJob, eventName?: string, payload?: object) => void;
}

/**
 * Shared hook for Hit component logic
 * Consolidates state, actions, and utilities used by both card and compact variants
 */
export type MenuOpenSource = 'desktop' | 'mobile' | null;

export function useHitShared({ hit, sendEvent }: UseHitSharedProps): HitSharedData {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const [menuOpenSource, setMenuOpenSource] = useState<MenuOpenSource>(null);
  const menuRefDesktop = useRef<HTMLDivElement>(null);
  const menuRefMobile = useRef<HTMLDivElement>(null);
  const isMenuOpen = menuOpenSource !== null;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDesktop = !menuRefDesktop.current?.contains(target);
      const isOutsideMobile = !menuRefMobile.current?.contains(target);
      
      // Check if click is inside the portal menu (menu is rendered via portal)
      const clickedElement = event.target as HTMLElement;
      const isInsidePortalMenu = clickedElement.closest('[data-application-state-menu="true"]') !== null;
      
      if (isOutsideDesktop && isOutsideMobile && !isInsidePortalMenu) {
        setMenuOpenSource(null);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);
  
  // Get state
  const state = useHitState({ hit });
  const { jobId, isSaved, isViewed, isApplied, isHidden, currentApplicationState, isToolsPage, savedJobPayload, appliedAt } = state;
  
  // Get toast
  let showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number, action?: ToastAction) => void = () => {};
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch {
    // Toast context not available (SSR or not wrapped in provider)
  }
  
  // Get actions
  const actions = useHitActions({
    hit,
    jobId,
    isSaved,
    isViewed,
    isApplied,
    isHidden,
    savedJobPayload,
    showToast,
    onMenuClose: () => setMenuOpenSource(null),
  });
  
  // Get search utilities
  const { getUrlForIndexUiState, pushIndexUiState } = usePushIndexUiState();
  const { filters } = useSearchFilters();
  const { query } = useSearchQuery();
  const { page } = useSearchPage();
  
  // Make refinement state helper
  const makeRefinementState = useCallback((facet: string, value: string | string[]): { query: string; refinementList: Record<string, string[]>; page: number } => {
    const currentRefinements = (filters as Record<string, string[]>) || {};
    const preserved = { ...currentRefinements };
    if (facet === 'jobType') {
      delete preserved['jobType'];
    }
    if (facet === 'vacationWeeks') {
      delete preserved['vacationWeeks'];
    }
    const singleValue = Array.isArray(value) ? value[0] : value;
    return {
      query: '',
      refinementList: {
        ...preserved,
        [facet]: [singleValue],
      },
      page: 0,
    };
  }, [filters]);
  
  // Current page URL origin — used for dev mode job link base URL
  let pageContext: ReturnType<typeof usePageContext> | null = null;
  try {
    pageContext = usePageContext();
  } catch {
    // PageContext not available (e.g. outside Vike)
  }
  const pageContextUrlOriginal =
    pageContext && typeof (pageContext as { urlOriginal?: string }).urlOriginal === 'string'
      ? (pageContext as { urlOriginal: string }).urlOriginal
      : undefined;
  const jobLinkBaseUrl = import.meta.env.DEV
    ? getDevSiteOrigin({ requestUrl: pageContextUrlOriginal })
    : BASE_URL;

  const jobUrl = useMemo(() => {
    const url = generateJobUrl({
      baseUrl: jobLinkBaseUrl,
      jobId: hit.objectID.toString(),
      locale: intl.locale as Locale,
      title: resolveJobUrlTitle(hit),
      company: resolveJobUrlCompany(hit),
      location: resolveJobUrlLocation(hit),
      remoteness: resolveJobUrlRemoteness(hit),
    });
    return toRelativePath(url);
  }, [hit, intl.locale, jobLinkBaseUrl]);
  
  // Get location data
  const locationData = useMemo(() => getLocationDisplayWithCount(hit), [hit]);
  const locationStr = useMemo(() => {
    if (Array.isArray(hit.location) && hit.location.length > 0) {
      return String(hit.location[0] || '');
    }
    return String(hit.location || '');
  }, [hit]);

  // TEC-482: structured (gazetteer) location display, gated on the same cookie flag the filter
  // UI uses, so a card pill links to the `regionTags` facet the flag-on filter row actually reads,
  // never to the old `location` facet that row no longer shows. Starts false during SSR/hydration
  // (useFeatureFlag) and flips post-mount, same as TopFilters/LocationFilterSection.
  const newLocationSystemEnabled = useNewLocationSystemEnabled();
  const structuredLocale: StructuredLocationLocale =
    intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';
  const selectedRegionTags = useMemo(() => {
    const raw = (filters as Record<string, unknown> | null | undefined)?.regionTags;
    if (Array.isArray(raw)) return raw.map(String);
    return typeof raw === 'string' && raw ? [raw] : [];
  }, [filters]);
  const structuredLocation = useMemo(
    () => (newLocationSystemEnabled ? resolveStructuredJobLocations(hit, { locale: structuredLocale, selectedRegionTags }) : null),
    [newLocationSystemEnabled, hit, structuredLocale, selectedRegionTags],
  );
  const remoteScopes = useMemo(
    () => (newLocationSystemEnabled ? resolveStructuredRemoteScopes(hit, structuredLocale) : []),
    [newLocationSystemEnabled, hit, structuredLocale],
  );
  // Kept alongside remoteScopes for any caller still expecting a single value (see HitSharedData).
  const remoteScope = remoteScopes[0] ?? null;
  const remotenessLabel = useMemo(
    // A remote-only job already shows its scope as the location pill; keep the work model plain then.
    () => formatRemotenessWithScopes(hit.remoteness, structuredLocation?.remoteScopeAsLocation ? [] : remoteScopes),
    [hit.remoteness, remoteScopes, structuredLocation],
  );
  
  const isTyomarkkinatori = useMemo(
    () => resolveSearchHitIsTyomarkkinatori(hit as unknown as Record<string, unknown>),
    [hit.isTyomarkkinatori, hit.url],
  );

  // Brand color styles (effective colour so Details button and border match SSR)
  const brandColour = resolveEffectiveBrandColour(hit.brandColour, isTyomarkkinatori);
  const brandStyles = useMemo(() => getBrandColorStyles(brandColour, isTyomarkkinatori), [brandColour, isTyomarkkinatori]);
  
  // Match score
  const { isAuthenticated, signIn } = useAuth();
  const userProfile = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const savedSearches = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  // TEC-404: only this userState read is migrated — jobForm/comparisonPreferences/recentSearches
  // stay on Redux (still-blocked, sync-engine-authoritative state, out of scope here). On the
  // client `isHydrated` was always true regardless of hydration status (the `typeof window` half
  // of the old `||`); `userStateData !== undefined` is the equivalent of the old
  // `hydratedFromSSR` flag — true once `UserProvider`'s `initialUserState` (or a client fetch) has
  // seeded the `userState` query cache.
  const { data: userStateData } = useUserState();
  const isHydrated = userStateData !== undefined || (typeof window !== 'undefined');
  
  const resolvedMatch = useMemo(
    () =>
      resolveJobMatchScore({
        job: hit,
        userProfile,
        comparisonPreferences,
        savedSearches,
        isHydrated,
      }),
    [hit, userProfile, comparisonPreferences, savedSearches, isHydrated]
  );

  const matchScoreData = resolvedMatch.breakdown;
  const matchScore = matchScoreData?.overall ?? null;
  const matchScoreMeta =
    resolvedMatch.source === 'recent-searches'
      ? { source: 'recent-searches' as const, bullets: resolvedMatch.searchInferenceBullets }
      : null;
  
  const handleLoginClick = useCallback(() => {
    trackEvent({
      name: 'auth_interaction',
      params: { action: 'sign_in_click', surface: 'match_donut', locale: intl.locale },
    });
    signIn();
  }, [signIn, intl.locale]);
  
  // Track browsed jobs
  const browsedJobRef = useBrowsedJobTracking(
    savedJobPayload,
    actions.recordBrowsedJob,
    isAuthenticated && !isToolsPage
  );
  
  // Application state button state
  const buttonState = useApplicationStateButtonState(currentApplicationState, isApplied);
  
  // Share handler
  const handleShare = useCallback(async () => {
    trackJobShare({
      jobId: hit.objectID,
      jobTitle: hit.normalisedTitle || hit.title || '',
      locale: intl.locale,
    });
    
    const url = generateJobUrl({
      baseUrl: jobLinkBaseUrl,
      jobId: hit.objectID.toString(),
      locale: intl.locale as Locale,
      title: resolveJobUrlTitle(hit),
      company: resolveJobUrlCompany(hit),
      location: resolveJobUrlLocation(hit),
      remoteness: resolveJobUrlRemoteness(hit),
    });
    
    const jobTitle = hit.normalisedTitle || hit.title || document.title;
    
    await sharePageUtil(
      url,
      jobTitle,
      () => {
        showToast(
          formatMessage({
            id: 'hit.linkCopied',
            defaultMessage: 'Link copied to clipboard!',
            description: 'Message shown when link is copied to clipboard',
          }),
          'success'
        );
      },
      (error) => {
        showToast(
          formatMessage({
            id: 'hit.shareError',
            defaultMessage: 'Something went wrong while trying to share this job.',
            description: "Message shown when there's an error sharing",
          }),
          'error',
          undefined,
          {
            label: formatMessage({ id: 'hit.shareError.retry', defaultMessage: 'Retry', description: 'Toast action that retries sharing the job after it failed' }),
            onClick: () => void handleShare(),
          }
        );
      }
    );
  }, [hit, intl, locationStr, showToast, jobLinkBaseUrl]);
  
  // Add share to actions
  const actionsWithShare = {
    ...actions,
    handleShare,
  };
  
  // IDs
  const articleId = `job-${hit.objectID}`;
  const titleId = `${articleId}-title`;
  const jobTitle = hit.normalisedTitle || hit.title || formatMessage({ id: 'hit.jobTitle', defaultMessage: 'Job', description: 'Fallback text when job title is not available, used as placeholder' });
  
  return {
    jobId,
    jobUrl,
    articleId,
    titleId,
    jobTitle,
    isSaved,
    isViewed,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage,
    savedJobPayload,
    appliedAt,
    brandColour,
    isTyomarkkinatori,
    useBrandColour: brandStyles.useBrandColour,
    leftBorderStyle: brandStyles.leftBorderStyle,
    matchScore,
    matchScoreData,
    matchScoreMeta,
    isAuthenticated,
    onLoginClick: handleLoginClick,
    actions: actionsWithShare,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
    showToast,
    browsedJobRef: browsedJobRef as React.RefObject<HTMLElement>,
    menuRefDesktop,
    menuRefMobile,
    menuOpenSource,
    setMenuOpenSource,
    isMenuOpen,
    setIsMenuOpen: (open: boolean) => setMenuOpenSource(open ? 'desktop' : null),
    buttonState,
    locationData,
    locationStr,
    structuredLocation,
    remoteScope,
    remoteScopes,
    remotenessLabel,
    filters,
    hit,
    sendEvent,
  };
}
